import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { fetchAllUsers, fetchCustomerPlots } from "../store/slices/authSlice";
import { fetchSocieties } from "../store/slices/societySlice";
import {
  transferPlotThunk,
  fetchTransferPlotsThunk,
} from "../store/slices/transferPlotSlice";
import { Calendar, Search, Percent, DollarSign, X, Printer } from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import printTransferLetter from "./TransferPrint";

const CustomDatePicker = ({ field, form, ...props }) => {
  const { setFieldValue } = form;
  const { name, value } = field;
  const { theme } = useSidebarContext();

  return (
    <div className="relative">
      <div className="absolute right-4 top-1/2 -translate-y-1/2">
        <Calendar className={`h-6 w-6 ${theme.tailwind.groupDisabled}`} />
      </div>
      <DatePicker
        {...props}
        selected={value ? new Date(value) : null}
        onChange={(date) => {
          setFieldValue(name, date);
        }}
        maxDate={new Date()}
        dateFormat="dd/MM/yyyy"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        wrapperClassName="w-full"
        className={`w-full h-12 ${theme.tailwind.bg2} ${theme.tailwind.text9} rounded-2xl px-6 border ${theme.tailwind.border15} ${theme.tailwind.focus2} focus:ring-2 ${theme.tailwind.focusRing3} transition-all duration-300`}
        placeholderText="Select transfer date...(DD/MM/YYYY)"
        scrollableYearDropdown
        yearDropdownItemNumber={15}
      />
    </div>
  );
};

const TransferPlotForm = () => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { activeSociety, gradient, theme } = useSidebarContext();

  const { users } = useSelector((state) => state.auth);
  const { plots: customerPlots } = useSelector((state) => state.auth);
  const { societies } = useSelector((state) => state.societies);

  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [selectedPlot, setSelectedPlot] = useState("");
  const [selectedPlotData, setSelectedPlotData] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // Customer name search
  const [plotSearchTerm, setPlotSearchTerm] = useState(""); // Plot number search
  const [isFocused, setIsFocused] = useState(false);

  // Fee type state
  const [feeType, setFeeType] = useState("none"); // 'none', 'percentage', 'fixed'
  const [feePercentage, setFeePercentage] = useState("");
  const [calculatedFee, setCalculatedFee] = useState(0);

  // Transfer success state for print
  const [transferSuccess, setTransferSuccess] = useState(false);
  const [transferData, setTransferData] = useState(null);

  // Dynamic validation based on fee type
  const TransferPlotSchema = Yup.object().shape({
    new_owner_id: Yup.string().required("Please select a customer"),
    plot_id: Yup.string().required("Please select a plot"),
    transfer_fee: feeType === "none"
      ? Yup.string().notRequired()
      : Yup.string()
        .matches(/^[\d,]*$/, "Transfer fee must be a valid number")
        .notRequired(),
    transfer_date: Yup.date()
      .required("Transfer date is required")
      .max(new Date(), "Transfer date cannot be in the future")
      .typeError("Please enter a valid date"),
  });

  useEffect(() => {
    dispatch(fetchAllUsers());
    dispatch(fetchSocieties());
  }, [dispatch]);

  // Get customers with SOLD plots only (fully paid)
  useEffect(() => {
    if (users?.length > 0 && activeSociety && societies?.length > 0) {
      // Find customers who have at least one "Booked" or "Sold" plot in this society
      const customersWithPlots = users.filter((user) => {
        const uId = user._id?.toString();
        const cId = user.customerData?._id?.toString();

        // Check if user is in this society
        if (!activeSociety.users?.some(u => u._id?.toString() === uId)) {
          return false;
        }

        // Check if user has any Booked/Sold plots in this society's plots
        // We check against both user ID and customer data ID because different parts of the system might store either
        const hasPlot = activeSociety.plots?.some(plot => {
          const pCustId = (plot.customer_id?._id || plot.customer_id)?.toString();
          const isOwned = pCustId && (pCustId === uId || pCustId === cId);
          const isValidStatus = plot.booking_status === "Booked" || plot.booking_status === "Sold";
          return isOwned && isValidStatus;
        });

        return hasPlot;
      });

      setCustomers(customersWithPlots);
      setFilteredCustomers(customersWithPlots);
    } else {
      setCustomers([]);
      setFilteredCustomers([]);
    }
  }, [users, activeSociety, societies]);

  // Search filter - separate customer name and plot number search
  useEffect(() => {
    if (searchTerm || plotSearchTerm) {
      const nameLower = searchTerm.toLowerCase().trim();
      const plotLower = plotSearchTerm.toLowerCase().trim();

      const filtered = customers.filter((customer) => {
        const uId = customer._id?.toString();
        const cId = customer.customerData?._id?.toString();

        // Search by customer name/CNIC/phone
        let matchesCustomer = true;
        if (nameLower) {
          const cnicRaw = customer.customerData?.contactInfo?.cnic?.replace(/-/g, '') || "";
          const phoneRaw = customer.customerData?.contactInfo?.phone?.replace(/-/g, '') || "";
          const searchRaw = searchTerm.replace(/-/g, '');

          matchesCustomer =
            customer.username?.toLowerCase().includes(nameLower) ||
            customer.customerData?.name?.toLowerCase().includes(nameLower) ||
            cnicRaw.includes(searchRaw) ||
            phoneRaw.includes(searchRaw);
        }

        // Search by plot number
        let matchesPlot = true;
        if (plotLower) {
          // Check if ANY of the plots belonging to this customer match the plot number
          matchesPlot = activeSociety.plots?.some(plot => {
            const pCustId = (plot.customer_id?._id || plot.customer_id)?.toString();
            const isOwned = pCustId && (pCustId === uId || pCustId === cId);
            const statusMatch = plot.booking_status === "Booked" || plot.booking_status === "Sold";
            const numberMatch = plot.plot_number?.toLowerCase().includes(plotLower);
            return isOwned && statusMatch && numberMatch;
          });
        }

        return matchesCustomer && matchesPlot;
      });
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, plotSearchTerm, customers, activeSociety]);

  useEffect(() => {
    if (selectedCustomer) {
      dispatch(fetchCustomerPlots(selectedCustomer));
    }
  }, [selectedCustomer, dispatch]);

  useEffect(() => {
    if (activeSociety) {
      setSearchTerm("");
      setPlotSearchTerm("");
      setSelectedCustomer("");
      setSelectedCustomerData(null);
      setSelectedPlot("");
      setSelectedPlotData(null);
      setFeeType("none");
      setFeePercentage("");
      setCalculatedFee(0);
      const societyCustomers = users.filter((user) =>
        activeSociety.users.some(
          (societyUser) =>
            societyUser._id === user._id && societyUser.role === "Customer"
        )
      );

      setCustomers(societyCustomers);
      setFilteredCustomers(societyCustomers);
    }
  }, [activeSociety, users]);

  // Calculate fee when percentage or plot changes
  useEffect(() => {
    if (feeType === "percentage" && feePercentage && selectedPlotData?.total_amount) {
      const fee = (parseFloat(feePercentage) / 100) * selectedPlotData.total_amount;
      setCalculatedFee(Math.round(fee));
    } else if (feeType === "none") {
      setCalculatedFee(0);
    }
  }, [feeType, feePercentage, selectedPlotData]);

  const getSocietyAndPlotDetails = (plotId) => {
    if (!societies || societies.length === 0) {
      return null;
    }

    for (let soc of societies) {
      const plot = soc.plots.find((plot) => plot._id === plotId);
      if (plot) {
        return {
          societyName: soc.name,
          plotNumber: plot.plot_number,
          plotType: plot.plot_type,
          block: plot.block,
          category: plot.category,
          bookingStatus: plot.booking_status,
          status: plot.status,
          total_amount: plot.price || 0,
        };
      }
    }
    return null;
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCustomerSelect = (customer, setFieldValue) => {
    setSelectedCustomer(customer.customerData?._id || customer._id);
    setSelectedCustomerData(customer);
    setSearchTerm(customer.customerData?.name || customer.username);
    setFieldValue("new_owner_id", customer._id);
    setFieldValue("plot_id", "");
    setFilteredCustomers([]);
    setIsFocused(false);
    setSelectedPlot("");
    setSelectedPlotData(null);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setTimeout(() => setIsFocused(false), 200);
  };

  const handlePlotSelect = (plot, setFieldValue) => {
    const details = getSocietyAndPlotDetails(plot._id);

    const isTransferable =
      details?.bookingStatus === "Booked" || details?.bookingStatus === "Sold";

    if (!isTransferable) {
      enqueueSnackbar(
        `This plot cannot be transferred. Current status: ${details?.bookingStatus}`,
        { variant: "error" }
      );
      return;
    }

    if (details?.status === "Installment") {
      enqueueSnackbar("Plots with installment status cannot be transferred.", {
        variant: "error",
      });
      return;
    }

    setSelectedPlot(plot._id);
    setSelectedPlotData({ ...plot, ...details });
    setFieldValue("plot_id", plot._id);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-PK", {
      style: "currency",
      currency: "PKR",
      minimumFractionDigits: 0
    }).format(amount || 0).replace("PKR", "Rs.");
  };

  const handlePrintTransfer = () => {
    printTransferLetter({
      transferData: {
        transfer_fee: calculatedFee || transferData?.transfer_fee || 0,
        fee_type: feeType,
        fee_percentage: feePercentage,
        transfer_date: transferData?.transfer_date || new Date()
      },
      previousOwner: selectedCustomerData?.customerData,
      newOwner: selectedCustomerData?.customerData, // Same customer gets registry
      plot: selectedPlotData,
      society: activeSociety
    });
  };

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    // Calculate final fee
    let finalFee = 0;
    if (feeType === "percentage") {
      finalFee = calculatedFee;
    } else if (feeType === "fixed" && values.transfer_fee) {
      finalFee = parseInt(values.transfer_fee.replace(/,/g, "")) || 0;
    }

    const formattedValues = {
      ...values,
      transfer_fee: finalFee,
      fee_type: feeType,
      fee_percentage: feeType === "percentage" ? parseFloat(feePercentage) : null,
      transfer_date: values.transfer_date,
    };

    dispatch(transferPlotThunk(formattedValues))
      .then((response) => {
        if (response.type.includes("fulfilled")) {
          enqueueSnackbar("Plot transfer (Registry) completed successfully!", {
            variant: "success",
          });

          setTransferSuccess(true);
          setTransferData({
            ...formattedValues,
            transfer_date: values.transfer_date
          });

          dispatch(fetchTransferPlotsThunk());
        } else if (response.type.includes("rejected")) {
          const errorMessage =
            response.payload?.message ||
            "Failed to transfer plot. Please try again.";
          enqueueSnackbar(errorMessage, { variant: "error" });
        }
      })
      .catch((error) => {
        console.error("Transfer failed - error:", error);
        const errorMessage =
          error?.payload?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to transfer plot. Please try again.";
        enqueueSnackbar(errorMessage, { variant: "error" });
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const resetFormState = () => {
    setTransferSuccess(false);
    setTransferData(null);
    setSelectedCustomer("");
    setSelectedCustomerData(null);
    setSelectedPlot("");
    setSelectedPlotData(null);
    setSearchTerm("");
    setFeeType("none");
    setFeePercentage("");
    setCalculatedFee(0);
  };

  // Success View
  if (transferSuccess) {
    return (
      <div className={`${gradient} rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] p-6`}>
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className={`w-20 h-20 ${theme.tailwind.bg25} rounded-full mx-auto flex items-center justify-center mb-6`}>
            <svg className="w-10 h-10 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className={`text-2xl font-bold ${theme.tailwind.text9} mb-2`}>Transfer Registry Complete!</h2>
          <p className={`${theme.tailwind.text4} mb-8`}>
            Plot {selectedPlotData?.plotNumber || selectedPlotData?.plot_number} registry has been successfully issued to {selectedCustomerData?.customerData?.name || selectedCustomerData?.username}
          </p>

          {calculatedFee > 0 && (
            <div className={`${theme.tailwind.bg25} rounded-xl p-4 mb-6 inline-block`}>
              <span className={`${theme.tailwind.text4} text-sm`}>Transfer Fee: </span>
              <span className={`${theme.tailwind.text9} font-bold text-lg`}>{formatCurrency(calculatedFee)}</span>
            </div>
          )}

          <div className="flex justify-center gap-4">
            <button
              onClick={handlePrintTransfer}
              className={`px-6 py-3 ${theme.tailwind.bgr7} text-white rounded-xl font-medium flex items-center gap-2 transition-all hover:shadow-lg`}
            >
              <Printer size={18} />
              Print Transfer Letter
            </button>
            <button
              onClick={resetFormState}
              className={`px-6 py-3 ${theme.tailwind.bg2} ${theme.tailwind.text9} border ${theme.tailwind.border15} rounded-xl font-medium transition-all hover:opacity-80`}
            >
              New Transfer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${gradient} rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] p-6`}
    >
      <div className="max-w-7xl mx-auto">
        <div className="space-y-8">
          {/* Header Section */}
          <div className="text-center space-y-6">
            <div className="relative">
              <div className="relative left-1/2 -translate-x-1/2 w-56 mb-2">
                <div
                  className={`px-4 py-3 ${theme.tailwind.bg1} backdrop-blur-xl rounded-full border ${theme.tailwind.border11} flex items-center justify-center gap-2`}
                >
                  {activeSociety?.society_image && (
                    <img
                      src={activeSociety.society_image}
                      alt={`${activeSociety?.name || "Society"} Logo`}
                      className="w-7 h-7 object-cover rounded-full"
                    />
                  )}
                  <p
                    className={`${theme.tailwind.text9} text-sm px-3 font-semibold`}
                  >
                    {activeSociety?.name || "Loading..."}
                  </p>
                </div>
              </div>

              <h1 className="text-4xl font-bold">
                <span
                  className={`${theme.tailwind.bgr6} bg-clip-text text-transparent`}
                >
                  TRANSFER PLOT REGISTRY
                </span>
              </h1>
              <p className={`${theme.tailwind.text4}`}>
                Issue ownership registry to customer
              </p>
            </div>
          </div>
          <div
            className={`backdrop-blur-xl bg-white/5 rounded-3xl border border-white/10 shadow-2xl p-8`}
          >
            <Formik
              initialValues={{
                plot_id: "",
                new_owner_id: "",
                transfer_fee: "",
                transfer_date: null,
              }}
              validationSchema={TransferPlotSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                <Form className="space-y-8">
                  <div className="relative">
                    {/* Search Inputs - Side by Side */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Customer Name Search */}
                      <div className="relative">
                        <label
                          className={`inline-block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                        >
                          Search by Customer Name/CNIC/Phone
                        </label>
                        <div className="relative">
                          <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            className={`w-full h-12 ${theme.tailwind.bg2} ${theme.tailwind.text9
                              } rounded-2xl px-6 border ${touched.new_owner_id && errors.new_owner_id
                                ? "border-red-500"
                                : `${theme.tailwind.border15} ${theme.tailwind.focus2}`
                              } focus:ring-2 ${theme.tailwind.focusRing3
                              } transition-all duration-300`}
                            placeholder="Enter name, CNIC or phone..."
                          />
                          <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            <Search
                              className={`h-5 w-5 ${theme.tailwind.text4}`}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Plot Number Search */}
                      <div className="relative">
                        <label
                          className={`inline-block ${theme.tailwind.text9} text-sm font-medium mb-2`}
                        >
                          Search by Plot Number
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            value={plotSearchTerm}
                            onChange={(e) => setPlotSearchTerm(e.target.value)}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            className={`w-full h-12 ${theme.tailwind.bg2} ${theme.tailwind.text9
                              } rounded-2xl px-6 border ${theme.tailwind.border15} ${theme.tailwind.focus2
                              } focus:ring-2 ${theme.tailwind.focusRing3
                              } transition-all duration-300`}
                            placeholder="Enter plot number..."
                          />
                          <div className="absolute right-4 top-1/2 -translate-y-1/2">
                            <Search
                              className={`h-5 w-5 ${theme.tailwind.text4}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <ErrorMessage
                      name="new_owner_id"
                      component="div"
                      className="text-red-500 text-sm mt-2"
                    />

                    {/* Dropdown Results */}
                    {isFocused && filteredCustomers.length > 0 && (
                      <div
                        className={`absolute w-full left-0 mt-2 py-2 ${theme.tailwind.bg39} backdrop-blur-xl border ${theme.tailwind.border15} rounded-2xl z-50 shadow-xl max-h-60 overflow-y-auto`}
                      >
                        {filteredCustomers.map((customer) => (
                          <div
                            key={customer._id}
                            onClick={() =>
                              handleCustomerSelect(customer, setFieldValue)
                            }
                            className={`px-6 py-3 ${theme.tailwind.bgHover7} cursor-pointer ${theme.tailwind.text9} transition-colors duration-200`}
                          >
                            <div className="font-medium">{customer.customerData?.name || customer.username}</div>
                            <div className={`text-xs ${theme.tailwind.text4}`}>
                              {customer.customerData?.contactInfo?.cnic} • {customer.customerData?.contactInfo?.phone}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Plots Grid */}
                  {selectedCustomer &&
                    customerPlots.length > 0 &&
                    (() => {
                      const transferablePlots = customerPlots.filter(
                        (plot) => {
                          const details = getSocietyAndPlotDetails(plot._id);
                          return (
                            details?.bookingStatus === "Booked" ||
                            details?.bookingStatus === "Sold"
                          );
                        }
                      );

                      if (transferablePlots.length === 0) {
                        return (
                          <div
                            className={`text-center py-8 px-4 ${theme.tailwind.bg25} rounded-2xl`}
                          >
                            <p
                              className={`${theme.tailwind.text9} font-medium`}
                            >
                              This customer has no plots available for
                              transfer.
                            </p>
                            <p
                              className={`${theme.tailwind.text4} text-sm mt-2`}
                            >
                              Only plots with "Booked" or "Sold" status can be
                              transferred.
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="space-y-6">
                          <div
                            className={`flex items-center gap-3 px-4 py-3 ${theme.tailwind.bg25} rounded-2xl`}
                          >
                            <div
                              className={`h-2 w-2 rounded-full ${theme.tailwind.bg40} animate-pulse`}
                            />
                            <p
                              className={`${theme.tailwind.text9} font-medium`}
                            >
                              Available Plots ({transferablePlots.length})
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {transferablePlots.map((plot) => {
                              const details = getSocietyAndPlotDetails(
                                plot._id
                              );

                              const isDisabled =
                                (details?.bookingStatus !== "Booked" &&
                                  details?.bookingStatus !== "Sold") ||
                                details?.status === "Installment";

                              return (
                                <div
                                  key={plot._id}
                                  onClick={() =>
                                    !isDisabled &&
                                    handlePlotSelect(plot, setFieldValue)
                                  }
                                  className={`relative group rounded-2xl border transition-all duration-500 ${isDisabled
                                    ? `bg-gray-800/50 border-gray-600 cursor-not-allowed opacity-60`
                                    : selectedPlot === plot._id
                                      ? `${theme.tailwind.bg25} ${theme.tailwind.border16} shadow-lg ${theme.tailwind.shadow1}`
                                      : `${theme.tailwind.bg31} border-white/5 ${theme.tailwind.hoverB2} cursor-pointer`
                                    }`}
                                >
                                  <div className="p-6 space-y-4">
                                    <div className="flex justify-between items-center">
                                      <span
                                        className={`px-4 py-1 rounded-full ${details?.bookingStatus ===
                                          "Booked" ||
                                          details?.bookingStatus === "Sold"
                                          ? `${theme.tailwind.bg25}`
                                          : "bg-gray-700"
                                          } text-sm font-medium ${theme.tailwind.text9
                                          }`}
                                      >
                                        Plot {details?.plotNumber}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div
                                        className={`p-3 rounded-xl ${theme.tailwind.bg2} border ${theme.tailwind.border12}`}
                                      >
                                        <p
                                          className={`text-sm ${theme.tailwind.text4}`}
                                        >
                                          Size
                                        </p>
                                        <p
                                          className={`${theme.tailwind.text9} font-medium`}
                                        >
                                          {details?.plotType} Marla
                                        </p>
                                      </div>
                                      <div
                                        className={`p-3 rounded-xl ${theme.tailwind.bg2} border ${theme.tailwind.border12}`}
                                      >
                                        <p
                                          className={`text-sm ${theme.tailwind.text4}`}
                                        >
                                          Block
                                        </p>
                                        <p
                                          className={`${theme.tailwind.text9} font-medium`}
                                        >
                                          {details?.block}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex justify-between items-center pt-2">
                                      <div>
                                        <p
                                          className={`text-sm ${theme.tailwind.text4}`}
                                        >
                                          Status
                                        </p>
                                        <p
                                          className={`font-medium ${theme.tailwind.text9}`}
                                        >
                                          {details?.bookingStatus}
                                        </p>
                                      </div>
                                      <div>
                                        <p
                                          className={`text-sm ${theme.tailwind.text4}`}
                                        >
                                          Category
                                        </p>
                                        <p
                                          className={`${theme.tailwind.text9} font-medium`}
                                        >
                                          {details?.category}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          <ErrorMessage
                            name="plot_id"
                            component="div"
                            className="text-red-500 text-sm mt-2"
                          />
                        </div>
                      );
                    })()}

                  {/* Transfer Fee Options & Date */}
                  {selectedPlot && (
                    <div className="space-y-6">
                      {/* Fee Type Selection */}
                      <div>
                        <label className={`inline-block ${theme.tailwind.text9} text-sm font-medium mb-3`}>
                          Transfer Fee (Optional)
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setFeeType("none");
                              setFieldValue("transfer_fee", "");
                              setCalculatedFee(0);
                            }}
                            className={`p-4 rounded-xl border-2 transition-all ${feeType === "none"
                              ? `${theme.tailwind.border16} ${theme.tailwind.bg25}`
                              : `${theme.tailwind.border15} ${theme.tailwind.bg2} hover:opacity-80`
                              }`}
                          >
                            <X size={24} className={`mx-auto mb-2 ${feeType === "none" ? theme.tailwind.text9 : theme.tailwind.text4}`} />
                            <span className={`block text-sm font-medium ${theme.tailwind.text9}`}>No Fee</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFeeType("percentage")}
                            className={`p-4 rounded-xl border-2 transition-all ${feeType === "percentage"
                              ? `${theme.tailwind.border16} ${theme.tailwind.bg25}`
                              : `${theme.tailwind.border15} ${theme.tailwind.bg2} hover:opacity-80`
                              }`}
                          >
                            <Percent size={24} className={`mx-auto mb-2 ${feeType === "percentage" ? theme.tailwind.text9 : theme.tailwind.text4}`} />
                            <span className={`block text-sm font-medium ${theme.tailwind.text9}`}>Percentage</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setFeeType("fixed")}
                            className={`p-4 rounded-xl border-2 transition-all ${feeType === "fixed"
                              ? `${theme.tailwind.border16} ${theme.tailwind.bg25}`
                              : `${theme.tailwind.border15} ${theme.tailwind.bg2} hover:opacity-80`
                              }`}
                          >
                            <DollarSign size={24} className={`mx-auto mb-2 ${feeType === "fixed" ? theme.tailwind.text9 : theme.tailwind.text4}`} />
                            <span className={`block text-sm font-medium ${theme.tailwind.text9}`}>Fixed Amount</span>
                          </button>
                        </div>
                      </div>

                      {/* Percentage Input */}
                      {feeType === "percentage" && (
                        <div className="space-y-2">
                          <label className={`inline-block ${theme.tailwind.text9} text-sm font-medium`}>
                            Percentage {selectedPlotData?.total_amount ? `(of ${formatCurrency(selectedPlotData.total_amount)})` : ''}
                          </label>
                          <div className="relative">
                            <input
                              type="number"
                              value={feePercentage}
                              onChange={(e) => setFeePercentage(e.target.value)}
                              placeholder="Enter percentage"
                              className={`w-full h-12 ${theme.tailwind.bg2} ${theme.tailwind.text9} rounded-2xl px-6 pr-12 border ${theme.tailwind.border15} ${theme.tailwind.focus2} focus:ring-2 ${theme.tailwind.focusRing3} transition-all duration-300`}
                            />
                            <span className={`absolute right-4 top-1/2 -translate-y-1/2 ${theme.tailwind.text4}`}>%</span>
                          </div>
                          {calculatedFee > 0 && (
                            <div className={`${theme.tailwind.bg25} rounded-xl p-3 mt-2`}>
                              <span className={`${theme.tailwind.text4} text-sm`}>Calculated Fee: </span>
                              <span className={`${theme.tailwind.text9} font-bold`}>{formatCurrency(calculatedFee)}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Fixed Amount Input */}
                      {feeType === "fixed" && (
                        <div className="space-y-2">
                          <label className={`inline-block ${theme.tailwind.text9} text-sm font-medium`}>
                            Fixed Amount (PKR)
                          </label>
                          <div className="relative">
                            <Field
                              type="text"
                              name="transfer_fee"
                              value={values.transfer_fee.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                              onChange={(e) => {
                                const rawValue = e.target.value.replace(/,/g, "");
                                setFieldValue("transfer_fee", rawValue);
                                setCalculatedFee(parseInt(rawValue) || 0);
                              }}
                              className={`w-full h-12 ${theme.tailwind.bg2} ${theme.tailwind.text9} rounded-2xl px-6 pr-16 border ${theme.tailwind.border15} ${theme.tailwind.focus2} focus:ring-2 ${theme.tailwind.focusRing3} transition-all duration-300`}
                              placeholder="Enter amount"
                            />
                            <span className={`absolute right-4 top-1/2 -translate-y-1/2 ${theme.tailwind.text4}`}>PKR</span>
                          </div>
                        </div>
                      )}

                      {/* Transfer Date */}
                      <div className="space-y-2">
                        <label className={`inline-block ${theme.tailwind.text9} text-sm font-medium`}>
                          Transfer Date
                        </label>
                        <Field
                          name="transfer_date"
                          component={CustomDatePicker}
                        />
                        {touched.transfer_date && errors.transfer_date && (
                          <div className="text-red-500 text-sm mt-2">
                            {errors.transfer_date}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className={`w-full ${theme.tailwind.bgr7} h-12 rounded-2xl text-white font-medium transition-all duration-300 hover:shadow-lg ${theme.tailwind.shadowhover3} hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-50 disabled:pointer-events-none`}
                    disabled={
                      isSubmitting || !selectedPlot || !values.transfer_date
                    }
                  >
                    {isSubmitting ? "Processing..." : "Complete Transfer Registry"}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div >
  );
};

export default TransferPlotForm;
